/* eslint-disable jsx-a11y/anchor-is-valid */
// Disable anchor-is-valid since scrollspy needs to break the rule
// Consider replacing scrollspy to align with better a11y
import React, { useEffect, useState, useMemo } from 'react';
import { debounce } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Scrollspy from 'react-scrollspy';

import Link from 'components/shared/Link';

import catalogModule from 'domains/catalogs';
import { useAppSelector } from 'utils/hooks';

import type { CatalogSelectorCategory } from 'types/catalog_types';

type CatalogSelectorProps = {
  categories: CatalogSelectorCategory[];
};

function CatalogSelector(props: CatalogSelectorProps) {
  const { categories } = props;
  const [scrollToTopButtonActive, setScrollTopButtonActive] = useState(false);

  const catalogs = useAppSelector(catalogModule.selectors.selectAll);

  const scrollspyItems = useMemo(() => categories.map((c) => c.id), [categories]);

  useEffect(() => {
    function scrollToTopHandler() {
      setScrollTopButtonActive(window.scrollY > 500);
    }
    const debouncedHandler = debounce(scrollToTopHandler, 300);

    document.addEventListener('scroll', debouncedHandler);
    document.body.classList.toggle('hideBranding', localStorage.getItem('hideBranding') === 'true');

    return function cleanup() {
      document.removeEventListener('scroll', debouncedHandler);
    };
  }, []);

  const handleCatalogDescriptionClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const buttonEl = e.currentTarget;
    buttonEl.classList.toggle('show-more');
    const showMoreSpan = buttonEl.querySelector('span.show-more-span');
    const showLessSpan = buttonEl.querySelector('span.show-less-span');
    const descriptionEl = buttonEl.previousElementSibling;

    descriptionEl?.classList.toggle('show-more');
    showMoreSpan?.classList.toggle('hide');
    showLessSpan?.classList.toggle('hide');
  };

  if (catalogs.length === 0) return null;

  return (
    <React.Fragment>
      <h1 className="screen-reader-text">Select a Catalog</h1>
      <div className="catalog_selector_container">
        <div className="catalog-selector-header">
          <div className="catalog-section__container">
            {/* needed to add weird extra layer of nav element here in order for Online Ordering link
              to work properly with scrollspy (otherwise Online Ordering defaults to "isCurrent" class
              while displaying Wall Displays products) */}
            <nav className="scrollspy-nav">
              <Link
                href="https://bayphoto.com/order/online-ordering/"
                className="scrollspy-nav__item"
              >
                Online Ordering&nbsp;
                <FontAwesomeIcon className="icon-dark" icon="chevron-right" />
              </Link>
              <Scrollspy
                items={scrollspyItems}
                currentClassName="isCurrent"
                componentTag="nav"
                offset={-60}
                className="scrollspy-nav"
              >
                {categories.map((category) => (
                  <Link key={category.id} href={`#${category.id}`} className="scrollspy-nav__item">
                    {category.name}
                  </Link>
                ))}
              </Scrollspy>
            </nav>
          </div>
        </div>
        <div className="catalog-section__container">
          {categories.map((category) => (
            <section key={category.id} className="catalog-section" id={category.id}>
              <header className="catalog-section__section-header">
                <h2 className="catalog-section__title">{category.name}</h2>
                <p className="catalog-section__subtitle">{category.description}</p>
              </header>
              <div className="online-ordering-grid">
                {category.catalogs.map((catalog) => (
                  <div key={catalog.name} className="online-ordering-grid-item">
                    <Link href={catalog.order_url}>
                      <img src={catalog.image} alt={catalog.image_alt} />
                    </Link>
                    <div className="catalog-section__footer">
                      <h3 className="title">{catalog.name}</h3>
                      <div className="tab-info">
                        <Link href={catalog.info_url}>
                          <FontAwesomeIcon icon="info-circle" fixedWidth />
                        </Link>
                        <Link href={catalog.order_url}>
                          <FontAwesomeIcon icon="shopping-cart" fixedWidth />
                        </Link>
                      </div>
                    </div>
                    {catalog.description && (
                      <div className="description_container">
                        <p className="description-content">{catalog.description}</p>
                        <button
                          id={`${catalog.name}-description-button`}
                          className="description-button"
                          onClick={(e) => handleCatalogDescriptionClick(e)}
                        >
                          <span className="show-more-span">... read more</span>
                          <span className="hide show-less-span">read less</span>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                ;
              </div>
            </section>
          ))}
          <section className="catalog-section catalog-section__bay-roes">
            <header className="catalog-section__header">
              <h2 className="catalog-section__title">
                Don&apos;t See What You&apos;re Looking For?
              </h2>
              <p className="catalog-section__subtitle">
                Try Bay ROES for the full selection of Bay Photo&apos;s products and services, in a
                powerful desktop application.
              </p>
            </header>
            <div style={{ textAlign: 'center' }}>
              <Link href="https://bayphoto.com/order/bay-roes">Try Bay ROES</Link>
            </div>
          </section>
        </div>
        <div
          className={`scrollDown_backToTop ${
            scrollToTopButtonActive ? 'scrollDown_backToTop--active' : ''
          }`}
        >
          <Link type="button" variant="regular" href="#">
            <FontAwesomeIcon icon="chevron-down" size="lg" />
          </Link>
        </div>
      </div>
      <div
        className={`scrollDown_backToTop ${
          scrollToTopButtonActive ? 'scrollDown_backToTop--active' : ''
        }`}
      >
        <Link type="button" variant="regular" href="#">
          <FontAwesomeIcon icon="chevron-down" size="lg" />
        </Link>
      </div>
    </React.Fragment>
  );
}

export default CatalogSelector;
